import React from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import LoadingButton from '@mui/lab/LoadingButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'

import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ChairRoundedIcon from '@mui/icons-material/ChairRounded'
import Diversity2RoundedIcon from '@mui/icons-material/Diversity2Rounded'
import EventIcon from '@mui/icons-material/Event'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import VaccinesRoundedIcon from '@mui/icons-material/VaccinesRounded'

export type ConfirmationStepProps = {
  doctor?: string
  meetingOnly?: boolean
  network?: string
  patient: string
  patientNames?: string[]
  isGroupConsultation?: boolean
  pds?: string
  reason?: string
  speciality: string
  startDate?: string
  waitingRoom?: string
  submitting?: boolean
  onSubmit?: () => void
}

const StyledTaskAltIcon = styled(TaskAltIcon)({
  color: 'green',
  fontSize: 30,
})

export function ConfirmationStep(props: ConfirmationStepProps) {
  const { doctor, meetingOnly = false, network, patient, patientNames, isGroupConsultation, pds, reason, speciality, startDate, waitingRoom, submitting = false, onSubmit } = props
  const { t } = useTranslation()

  const isStepCompleted = patient && speciality && (doctor || meetingOnly || network || waitingRoom)

  return (
    <Container maxWidth="sm" data-testid="confirmation-step">
      <Paper sx={{ mb: 6 }}>
        <Stack direction="row" justifyContent="center" alignItems="center">
          <p>{t('page.createConsultation.step.confirmation.title')}</p>
        </Stack>

        <List disablePadding id="recap">
          <ListItem
            secondaryAction={
              <IconButton edge="end">
                <StyledTaskAltIcon />
              </IconButton>
            }
          >
            <ListItemAvatar>
              <Avatar>
                <AccountCircleIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={t('page.createConsultation.step.confirmation.field.patient')}
              secondary={
                isGroupConsultation && patientNames && patientNames.length > 0
                  ? patientNames.join(', ')
                  : patient
              }
            />
          </ListItem>
          <Divider />
          {doctor && (
            <>
              <ListItem
                secondaryAction={
                  <IconButton edge="end">
                    <StyledTaskAltIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <AccountCircleIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={t('page.createConsultation.step.confirmation.field.doctor')} secondary={doctor} />
              </ListItem>
              <Divider />
            </>
          )}
          {pds && (
            <>
              <ListItem
                secondaryAction={
                  <IconButton edge="end">
                    <StyledTaskAltIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <AccountCircleIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={t('page.createConsultation.step.confirmation.field.pds')} secondary={pds} />
              </ListItem>
              <Divider />
            </>
          )}
          {network && (
            <>
              <ListItem
                secondaryAction={
                  <IconButton edge="end">
                    <StyledTaskAltIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <Diversity2RoundedIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={t('page.createConsultation.step.confirmation.field.network')} secondary={network} />
              </ListItem>
              <Divider />
            </>
          )}
          {waitingRoom && (
            <>
              <ListItem
                secondaryAction={
                  <IconButton edge="end">
                    <StyledTaskAltIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <ChairRoundedIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={t('page.createConsultation.step.confirmation.field.waitingRoom')} secondary={waitingRoom} />
              </ListItem>
              <Divider />
            </>
          )}
          <ListItem
            secondaryAction={
              <IconButton edge="end">
                <StyledTaskAltIcon />
              </IconButton>
            }
          >
            <ListItemAvatar>
              <Avatar>
                <MonitorHeartRoundedIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText primary={t('page.createConsultation.step.confirmation.field.speciality')} secondary={speciality} />
          </ListItem>
          <Divider />
          {reason && (
            <>
              <ListItem
                secondaryAction={
                  <IconButton edge="end">
                    <StyledTaskAltIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <InfoRoundedIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={t('page.createConsultation.step.confirmation.field.reason')} secondary={reason} />
              </ListItem>
              <Divider />
            </>
          )}
          {startDate && (
            <>
              <ListItem
                secondaryAction={
                  <IconButton edge="end">
                    <StyledTaskAltIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <EventIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={t('page.createConsultation.step.confirmation.field.startDate')} secondary={startDate} />
              </ListItem>
              <Divider />
            </>
          )}
          <ListItem
            secondaryAction={
              <IconButton edge="end">
                <StyledTaskAltIcon />
              </IconButton>
            }
          >
            <ListItemAvatar>
              <Avatar>
                <VaccinesRoundedIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText primary={t('page.createConsultation.step.confirmation.field.meetingOnly', { context: !!meetingOnly ? 'true' : 'false' })} />
          </ListItem>
          <Divider />
        </List>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3, pb: 3 }}>
          <LoadingButton id="confirmation-submit" loading={submitting} onClick={onSubmit} variant="contained" disabled={!isStepCompleted}>
            {t('common:button.submit.label')}
          </LoadingButton>
        </Box>
      </Paper>
    </Container>
  )
}

export default ConfirmationStep
